import React from "react";
import ReactDOM from "react-dom";

import { ImgCollapse } from "@images/ImgCollapse";

export type DropdownOption = {
  value: string;
  label: string;
  title?: string;
  icon?: React.ReactNode;
  right?: string;
  disabled?: boolean;
};

type DropdownPickerProps = {
  options: DropdownOption[];
  value: string;
  onSelect: (value: string) => void;
};

/**
 * Compact single-button dropdown picker. The menu is rendered in a portal at
 * the document root (position: fixed) so the parent's overflow:hidden can't
 * clip it. It flies UP above the button, opens on hover (desktop) or tap
 * (mobile), and hides 500ms after the pointer leaves (fast moves between the
 * button and the menu don't close it).
 */
class DropdownPicker extends React.Component<DropdownPickerProps> {
  /** menuH = measured menu height (0 until first open render). */
  state = { open: false, menuH: 0 };
  /** Whether the menu was opened by tap (stays open until outside click / select). */
  private _tapOpen = false;
  private _hideTimer: ReturnType<typeof setTimeout> | null = null;
  private _menuPos = { top: 0, left: 0, width: 0 };
  private btnRef: HTMLButtonElement | null = null;

  componentDidMount() {
    document.addEventListener('click', this._onDocClick);
  }

  componentWillUnmount() {
    document.removeEventListener('click', this._onDocClick);
    if (this._hideTimer) clearTimeout(this._hideTimer);
  }

  private _clearHideTimer() {
    if (this._hideTimer) { clearTimeout(this._hideTimer); this._hideTimer = null; }
  }

  private _open(tap: boolean) {
    this._clearHideTimer();
    this._tapOpen = tap;
    this.setState({ open: true });
  }

  /** Delayed hide (500ms) so fast moves between button and menu don't close it. */
  private _scheduleClose() {
    this._clearHideTimer();
    this._hideTimer = setTimeout(() => {
      this._hideTimer = null;
      this._tapOpen = false;
      this.setState({ open: false });
    }, 500);
  }

  private _close() {
    this._clearHideTimer();
    this._tapOpen = false;
    this.setState({ open: false });
  }

  private _onEnter = () => {
    if (window.matchMedia?.('(hover: none), (pointer: coarse)').matches) return; // touch: tap only
    this._open(false);
  };

  private _onLeave = () => {
    if (this._tapOpen) return;
    if (this.state.open) this._scheduleClose();
  };

  private _onClick = () => {
    if (this._tapOpen && this.state.open) {
      this._close(); // tap on the open button toggles it closed
      return;
    }
    this._open(true);
  };

  /** Menu stays open while the pointer is over it (bridge for the 8px gap). */
  private _onMenuEnter = () => {
    if (!this._tapOpen) this._clearHideTimer();
  };

  private _onMenuLeave = () => {
    if (!this._tapOpen) this._scheduleClose();
  };

  private _onDocClick = (e: MouseEvent) => {
    if (!this.state.open) return;
    const t = e.target as HTMLElement;
    if (t?.closest?.('[data-menu]')) return; // inside the portaled menu
    this._close();
  };

  render() {
    const { options, value, onSelect } = this.props;
    const current = options.find(o => o.value === value) ?? options[0];
    const disabled = !!(current && current.disabled);

    // Fly-up position: above the button, 8px gap, clamped into the viewport.
    // On the very first open the menu height is unknown (menuH=0) — render it
    // once off-screen to measure, then re-render at the correct position.
    let menu = null;
    if (this.state.open) {
      const measured = this.state.menuH > 0;
      if (this.btnRef) {
        const r = this.btnRef.getBoundingClientRect();
        const top = measured ? Math.max(8, r.top - this.state.menuH - 8) : -9999;
        this._menuPos = { top, left: measured ? r.left : 0, width: r.width };
      }
      menu = ReactDOM.createPortal(
        <div
          data-menu
          className="size-dropdown-menu"
          role="menu"
          style={{ top: this._menuPos.top, left: this._menuPos.left, minWidth: this._menuPos.width || undefined }}
          ref={el => { if (el && el.offsetHeight !== this.state.menuH) this.setState({ menuH: el.offsetHeight }); }}
          onMouseEnter={this._onMenuEnter}
          onMouseLeave={this._onMenuLeave}
        >
          {options.map(o => (
            <button
              key={o.value}
              type="button"
              role="menuitem"
              className={'dropdown-item' + (value === o.value ? ' active' : '')}
              disabled={o.disabled}
              title={o.title}
              onClick={() => { onSelect(o.value); this._close(); }}
            >
              {o.icon && <span className="layout-icon">{o.icon}</span>}
              <span className="dropdown-item-label">{o.label}</span>
              {o.right && <span className="dropdown-item-ratio">{o.right}</span>}
            </button>
          ))}
        </div>,
        document.body
      );
    }

    return (
      <div className={'size-dropdown' + (this.state.open ? ' open' : '')} data-menu>
        <button
          ref={el => { this.btnRef = el; }}
          type="button"
          className="dropdown-current"
          onClick={this._onClick}
          onMouseEnter={this._onEnter}
          onMouseLeave={this._onLeave}
          title={current?.title}
          disabled={disabled}
          aria-haspopup="menu"
          aria-expanded={this.state.open}
        >
          {current?.icon && <span className="layout-icon">{current.icon}</span>}
          <span className="dropdown-label">{current?.label}</span>
          {current?.right && <span className="dropdown-item-ratio">{current.right}</span>}
          <span className="dropdown-caret"><ImgCollapse /></span>
        </button>
        {menu}
      </div>
    );
  }
}

export default DropdownPicker;
